import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import {
  Button,
  Checkbox,
  CircularProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { usersUpdateTwinResponder } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { Feature, OnboardingStep, TwinResponder, TwinResponderUpdateStatus } from 'api/model'
import { COLORS } from 'app-constants'
import withOnboardingStep from 'components/FTUE/withOnboardingStep'
import JelledModal from 'components/JelledModal'
import JelledSwitch from 'components/JelledSwitch'
import SetupPermissionsModal from 'components/Permissions/SetupPermissionsModal'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { useAdvancedSettings } from 'hooks/use-email-responder'
import { useActiveOrganization } from 'hooks/use-organization'
import { useFetchCheckPermissions } from 'hooks/use-permission'
import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { authenticatedUserAtom, organizationFamily, toastAtom } from 'state'
import { PermissionFeature, featurePermissionsAtomFamily } from 'state/permission'
import { capitalizeOnlyFirstLetter } from 'utils'

export function getLabelDisplayText(text: string) {
  return capitalizeOnlyFirstLetter(text.replace('CATEGORY_', ''))
}

export const LabelSelectComponent = ({
  label,
  setLabel,
  option,
  disabled,
  setHasChanges,
}: {
  label: string
  setLabel: React.Dispatch<React.SetStateAction<string>>
  option: string
  disabled: boolean
  setHasChanges: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const isMobile = useCheckMobileScreen()
  const handleChange = (event: SelectChangeEvent) => {
    setLabel(event.target.value)
    setHasChanges(true)
  }

  return (
    <Stack width="126px" mx={isMobile ? 4 : 8}>
      <Select
        disabled={disabled}
        value={label}
        onChange={handleChange}
        displayEmpty
        inputProps={{ 'aria-label': 'Without label' }}
        style={{
          fontSize: '12px',
          fontWeight: 500,
          lineHeight: '16px',
          width: '126px',
        }}>
        <MenuItem value="">None</MenuItem>
        <MenuItem value={option}>{option}</MenuItem>
      </Select>
    </Stack>
  )
}

export const JELLED_EMAIL_DOMAINS = ['jelled.io', 'jelled.ai']

export const AdvancedSettings = ({
  moveSalesEmailsToLabel,
  setMoveSalesEmailsToLabel,
  moveNoReplyEmailsToLabel,
  setMoveNoReplyEmailsToLabel,
  labelForDraftEmails,
  setLabelForDraftEmails,
  labelForSalesEmails,
  setLabelForSalesEmails,
  labelForNoReplyEmails,
  setLabelForNoReplyEmails,
  advancedSettingsFeaturePermissions,
  allowVisibilityToggle,
  setHasChanges,
}: {
  moveSalesEmailsToLabel: boolean
  setMoveSalesEmailsToLabel: React.Dispatch<React.SetStateAction<boolean>>
  moveNoReplyEmailsToLabel: boolean
  setMoveNoReplyEmailsToLabel: React.Dispatch<React.SetStateAction<boolean>>
  labelForDraftEmails: string
  setLabelForDraftEmails: React.Dispatch<React.SetStateAction<string>>
  labelForSalesEmails: string
  setLabelForSalesEmails: React.Dispatch<React.SetStateAction<string>>
  labelForNoReplyEmails: string
  setLabelForNoReplyEmails: React.Dispatch<React.SetStateAction<string>>
  advancedSettingsFeaturePermissions?: PermissionFeature | null
  allowVisibilityToggle: boolean
  setHasChanges: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const isMobile = useCheckMobileScreen()
  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  // permissions state
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState<boolean>(false)
  const onClosePermissionsModal = () => {
    setIsPermissionsModalOpen(false)
  }

  const acceptedPermissions = useMemo(() => {
    return advancedSettingsFeaturePermissions?.acceptedPermissions ?? []
  }, [advancedSettingsFeaturePermissions])
  const missingPermissions = useMemo(() => {
    return advancedSettingsFeaturePermissions?.missingPermissions ?? []
  }, [advancedSettingsFeaturePermissions])

  const renderContent = () => {
    return (
      <Stack>
        {!allowVisibilityToggle && (
          <Typography variant="h5" mb={1}>
            Advanced settings
          </Typography>
        )}

        <Stack direction={isMobile ? 'column' : 'row'} alignItems="center">
          <Typography
            my={1}
            variant="pXS"
            sx={{
              color: COLORS.GRAY_3,
            }}>
            Apply labels to emails processed and classified by Jelled.ai and select whether you want to move labeled
            emails out of the inbox to a specific label. Additional permissions are required to enable this feature.{' '}
          </Typography>

          {missingPermissions.length > 0 && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setIsPermissionsModalOpen(true)
              }}
              sx={{
                flex: '0 0 146px',
                marginLeft: '16px',
                maxHeight: '32px',
                color: COLORS.GRAY_2,
                border: `1px solid ${COLORS.GRAY_5}`,
              }}>
              Grant permissions
            </Button>
          )}
        </Stack>
        <Stack
          py={2}
          px={!allowVisibilityToggle ? 0 : 2}
          borderRadius="8px"
          spacing={2}
          sx={{
            background: COLORS.GRAY_8,
          }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-start"
            flexWrap="wrap"
            gap={isMobile ? '8px' : 0}>
            <Typography
              variant="pXS"
              sx={{
                color: COLORS.GRAY_2,
                width: '167px',
              }}>
              Drafts generated by Jelled.ai
            </Typography>

            <LabelSelectComponent
              label={labelForDraftEmails}
              setLabel={setLabelForDraftEmails}
              option="Jelled/Drafts"
              disabled={missingPermissions.length > 0}
              setHasChanges={setHasChanges}
            />

            {!isMobile && <Stack width="208px" />}
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-start"
            flexWrap="wrap"
            gap={isMobile ? '8px' : 0}>
            <Typography
              variant="pXS"
              sx={{
                color: COLORS.GRAY_2,
                width: '167px',
              }}>
              Sales outreach emails
            </Typography>

            <LabelSelectComponent
              label={labelForSalesEmails}
              setLabel={setLabelForSalesEmails}
              option="Jelled/Sales-outreach"
              disabled={missingPermissions.length > 0}
              setHasChanges={setHasChanges}
            />

            <Stack direction="row" alignItems="center" spacing={1} width="150px">
              <Checkbox
                disabled={!labelForSalesEmails}
                color="primary"
                checked={moveSalesEmailsToLabel}
                onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                  setMoveSalesEmailsToLabel(checked)
                  setHasChanges(true)
                }}
                sx={{
                  marginRight: '0',
                  padding: 0,
                }}
              />
              <Typography
                variant="pXS"
                sx={{
                  color: labelForSalesEmails ? COLORS.GRAY_2 : COLORS.GRAY_4,
                }}>
                Move emails to label
              </Typography>
            </Stack>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-start"
            flexWrap="wrap"
            gap={isMobile ? '8px' : 0}>
            <Stack direction="row" alignItems="center" spacing={1} width="167px">
              <Typography
                variant="pXS"
                sx={{
                  color: COLORS.GRAY_2,
                }}>
                No-reply emails
              </Typography>

              <Tooltip
                title="No-reply emails are emails that do not require a response based on your response patterns."
                enterTouchDelay={0}>
                <InfoOutlined
                  sx={{
                    height: '16px',
                    width: '16px',
                    color: COLORS.GRAY_3,
                    ml: 1,
                  }}
                />
              </Tooltip>
            </Stack>

            <LabelSelectComponent
              label={labelForNoReplyEmails}
              setLabel={setLabelForNoReplyEmails}
              option="Jelled/No-reply"
              disabled={missingPermissions.length > 0}
              setHasChanges={setHasChanges}
            />

            <Stack direction="row" alignItems="center" spacing={1} width="150px">
              <Checkbox
                disabled={!labelForNoReplyEmails}
                color="primary"
                checked={moveNoReplyEmailsToLabel}
                onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                  setMoveNoReplyEmailsToLabel(checked)
                  setHasChanges(true)
                }}
                sx={{
                  marginRight: '0',
                  padding: 0,
                }}
              />
              <Typography
                variant="pXS"
                sx={{
                  color: labelForNoReplyEmails ? COLORS.GRAY_2 : COLORS.GRAY_4,
                }}>
                Move emails to label
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        {isPermissionsModalOpen && (
          <SetupPermissionsModal
            additionalParams={{
              isSettingsModalOpen: 'true', // used to open the settings modal after permissions are granted
              isActivateEmailResponderActive: 'true', // used to turn the toggle on after permissions are granted
            }}
            onClose={onClosePermissionsModal}
            acceptedPermissions={acceptedPermissions}
            missingPermissions={missingPermissions}
            feature={Feature.advanced_email_responder}
          />
        )}
      </Stack>
    )
  }

  // we have a special experience that we show in EmailSettings page where we render the advanced settings
  if (!allowVisibilityToggle) {
    return renderContent()
  }

  return (
    <Stack mt={3}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={() => {
          setIsExpanded(!isExpanded)
        }}
        sx={{
          cursor: 'pointer',
        }}>
        <Typography
          variant="pXS"
          sx={{
            color: COLORS.GRAY_2,
          }}>
          Advanced settings
        </Typography>

        {isExpanded ? (
          <ArrowDropUpIcon
            sx={{
              color: COLORS.GRAY_2,
            }}
          />
        ) : (
          <ArrowDropDownIcon
            sx={{
              color: COLORS.GRAY_2,
            }}
          />
        )}
      </Stack>
      {isExpanded && renderContent()}
      {isPermissionsModalOpen && (
        <SetupPermissionsModal
          additionalParams={{
            isSettingsModalOpen: 'true', // used to open the settings modal after permissions are granted
            isActivateEmailResponderActive: 'true', // used to turn the toggle on after permissions are granted
          }}
          onClose={onClosePermissionsModal}
          acceptedPermissions={acceptedPermissions}
          missingPermissions={missingPermissions}
          feature={Feature.advanced_email_responder}
        />
      )}
    </Stack>
  )
}

const SettingsModal = ({
  isLoadingLabels,
  onClose,
  twinId,
  twinResponder,
  setTwinResponder,
  updateOnboardingStep,
}: {
  isLoadingLabels: boolean
  onClose: () => void
  twinId: string
  twinResponder: TwinResponder | null
  setTwinResponder: React.Dispatch<React.SetStateAction<TwinResponder | null>>
  updateOnboardingStep: () => void
}) => {
  const [activeOrganization] = useActiveOrganization()
  const organization = useRecoilValue(organizationFamily(activeOrganization ?? ''))
  const isMobile = useCheckMobileScreen()

  const user = useRecoilValue(authenticatedUserAtom)
  const setToast = useSetRecoilState(toastAtom)

  const [searchParams] = useSearchParams()

  // permissions state
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState<boolean>(false)
  const onClosePermissionsModal = () => {
    setIsPermissionsModalOpen(false)
    // we force the toggle to be off when the user closes permission modal
    setResponderStatus(TwinResponderUpdateStatus.inactive)
  }
  const gmailPermissions = useRecoilValue(featurePermissionsAtomFamily(Feature.email_responder))
  const hasPermissions = gmailPermissions?.isPermitted ?? false
  const acceptedPermissions = gmailPermissions?.acceptedPermissions ?? []
  const missingPermissions = gmailPermissions?.missingPermissions ?? []

  // we need to make an additional feature permissions call specifically for advanced settings
  const { featurePermissions: advancedSettingsFeaturePermissions, isLoading: isLoadingPermissions } =
    useFetchCheckPermissions(Feature.advanced_email_responder)

  const [responderStatus, setResponderStatus] = useState<TwinResponderUpdateStatus>(TwinResponderUpdateStatus.inactive)
  const onTwinResponderStatusChange = async (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (hasPermissions) {
      setResponderStatus(checked ? TwinResponderUpdateStatus.active : TwinResponderUpdateStatus.inactive)
    }

    if (checked && !hasPermissions) {
      setIsPermissionsModalOpen(true)
    }
  }

  // for advanced settings
  const {
    labelForSalesEmails,
    setLabelForSalesEmails,
    moveSalesEmailsToLabel,
    setMoveSalesEmailsToLabel,
    labelForNoReplyEmails,
    setLabelForNoReplyEmails,
    moveNoReplyEmailsToLabel,
    setMoveNoReplyEmailsToLabel,
    labelForDraftEmails,
    setLabelForDraftEmails,
    isAutoSendEnabled,
    setIsAutoSendEnabled,
  } = useAdvancedSettings()

  useEffect(() => {
    // when twin responder is fetched, we set the advanced settings
    if (twinResponder) {
      setMoveSalesEmailsToLabel(!twinResponder.sales_label_keep_inbox ?? false)
      setMoveNoReplyEmailsToLabel(!twinResponder.noreply_label_keep_inbox ?? false)
      setLabelForDraftEmails(twinResponder.draft_label ?? '')
      setLabelForSalesEmails(twinResponder.sales_label ?? '')
      setLabelForNoReplyEmails(twinResponder.noreply_label ?? '')
      setIsAutoSendEnabled(twinResponder.auto_send_enabled ?? false)
    }
  }, [
    twinResponder,
    setMoveSalesEmailsToLabel,
    setMoveNoReplyEmailsToLabel,
    setLabelForDraftEmails,
    setLabelForSalesEmails,
    setLabelForNoReplyEmails,
    setIsAutoSendEnabled,
  ])

  useEffect(() => {
    if (searchParams.get('isActivateEmailResponderActive') === 'true') {
      setResponderStatus(TwinResponderUpdateStatus.active)
    } else if (twinResponder) {
      setResponderStatus(twinResponder.status)
    }
  }, [twinResponder, searchParams])

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitCount, setSubmitCount] = useState(0)

  const onUpdateSettings = async () => {
    setIsSubmitting(true)
    setSubmitCount(submitCount + 1)

    if (!activeOrganization || !twinId) {
      return null
    }

    try {
      // update twin responder status
      await usersUpdateTwinResponder(activeOrganization, twinId, {
        status: responderStatus,
        noreply_label: labelForNoReplyEmails, // if label is empty, set as undefined
        noreply_label_keep_inbox: labelForNoReplyEmails === '' ? undefined : !moveNoReplyEmailsToLabel,
        sales_label: labelForSalesEmails,
        sales_label_keep_inbox: labelForSalesEmails === '' ? undefined : !moveSalesEmailsToLabel,
        draft_label: labelForDraftEmails,
        auto_send_enabled: isAutoSendEnabled,
      })
      // hydrate recoil with updated twin responder status
      setTwinResponder((prev) => {
        if (prev) {
          return {
            ...prev,
            status: responderStatus,
            noreply_label: labelForNoReplyEmails || undefined,
            noreply_label_keep_inbox: labelForNoReplyEmails === '' ? undefined : !moveNoReplyEmailsToLabel,
            sales_label: labelForSalesEmails || undefined,
            sales_label_keep_inbox: labelForSalesEmails === '' ? undefined : !moveSalesEmailsToLabel,
            draft_label: labelForDraftEmails || undefined,
            auto_send_enabled: isAutoSendEnabled,
          }
        }
        return null
      })

      // update onboarding step if it has not yet been set
      const isEmailResponderCompleted = user?.onboarding_steps_completed.includes(OnboardingStep.email_responder)
      if (!isEmailResponderCompleted) {
        updateOnboardingStep()
      }

      setToast({ title: 'Updated email responder settings', severity: 'success' })
      onClose()
    } catch (error) {
      setToast({ title: 'Failed to update email responder settings', severity: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoadingLabels || isLoadingPermissions) {
    return <CircularProgress size="24px" thickness={4} style={{ color: COLORS.JELLED_PRIMARY_BLUE }} />
  }

  // we only want to enable auto send for the organization with jelled.io or jelled.ai email_domain
  const isJelledEmailDomain = JELLED_EMAIL_DOMAINS.includes(organization?.email_domain ?? '')

  return (
    <>
      <JelledModal
        onClose={onClose}
        isOpen={true}
        width={isMobile ? '100%' : '675px'}
        modalStyle={{
          backgroundColor: COLORS.PURE_WHITE,
          marginLeft: isMobile ? '16px' : 0,
          marginRight: isMobile ? '16px' : 0,
        }}>
        <Stack>
          <Typography mb={2} variant="h4" color="secondary">
            Automatic drafts
          </Typography>

          <Typography
            variant="pXS"
            sx={{
              color: COLORS.GRAY_3,
            }}>
            Get intelligently drafted emails that incorporate your twin’s knowledge base. Responses will be drafted for
            all incoming emails addressed to you starting from today.
          </Typography>

          <JelledSwitch
            disabled={isSubmitting}
            label={`Activate automatic draft generation`}
            checked={responderStatus === TwinResponderUpdateStatus.active}
            onChange={onTwinResponderStatusChange}
            style={{
              color: COLORS.GRAY_0,
              marginTop: '24px',
              width: '320px',
            }}
          />

          {isJelledEmailDomain && (
            <JelledSwitch
              disabled={isSubmitting}
              label={`Send draft emails automatically`}
              checked={isAutoSendEnabled}
              onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                setIsAutoSendEnabled(checked)
              }}
              style={{
                color: COLORS.GRAY_0,
                marginTop: '24px',
                width: '320px',
              }}
            />
          )}

          <AdvancedSettings
            moveSalesEmailsToLabel={moveSalesEmailsToLabel}
            setMoveSalesEmailsToLabel={setMoveSalesEmailsToLabel}
            moveNoReplyEmailsToLabel={moveNoReplyEmailsToLabel}
            setMoveNoReplyEmailsToLabel={setMoveNoReplyEmailsToLabel}
            labelForDraftEmails={labelForDraftEmails}
            setLabelForDraftEmails={setLabelForDraftEmails}
            labelForSalesEmails={labelForSalesEmails}
            setLabelForSalesEmails={setLabelForSalesEmails}
            labelForNoReplyEmails={labelForNoReplyEmails}
            setLabelForNoReplyEmails={setLabelForNoReplyEmails}
            advancedSettingsFeaturePermissions={advancedSettingsFeaturePermissions}
            allowVisibilityToggle={true}
            setHasChanges={() => {}}
          />

          <Stack direction={isMobile ? 'column' : 'row'} justifyContent="flex-end" spacing={2} mt={4}>
            <Stack direction="row" alignItems="center" spacing={1} alignSelf="flex-end">
              <Button
                onClick={() => onClose()}
                size="medium"
                sx={{
                  color: 'primary.main',
                }}>
                Cancel
              </Button>
              <Button
                disabled={isSubmitting}
                color="primary"
                variant="contained"
                size="medium"
                onClick={onUpdateSettings}>
                Save
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </JelledModal>
      {isPermissionsModalOpen && (
        <SetupPermissionsModal
          additionalParams={{
            isSettingsModalOpen: 'true', // used to open the settings modal after permissions are granted
            isActivateEmailResponderActive: 'true', // used to turn the toggle on after permissions are granted
          }}
          onClose={onClosePermissionsModal}
          acceptedPermissions={acceptedPermissions}
          missingPermissions={missingPermissions}
          feature={Feature.email_responder}
        />
      )}
    </>
  )
}

export default withOnboardingStep(SettingsModal, OnboardingStep.email_responder)
